import { Schema } from "effect"
import {
  AgentKind,
  Evidence,
  ModelProvider,
  ModelsByAgent,
  Owned,
  Principal,
  Taint,
  UsageProvider,
} from "./common"
import { LoadedDocument } from "./documents"
import { WakeReason } from "./domain/agentlife"
import { Blocker, CloseResult, Hands, ImpossibleReason } from "./domain/item"
import { GitSha } from "./github"
import {
  AgentId,
  AreaId,
  CommentId,
  CredentialId,
  Epoch,
  GoalId,
  ItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProposalId,
  RunId,
  RunnerId,
  Sha256,
  ShortRef,
  Slug,
  WakeId,
  WorkerRef,
  WorkRequestId,
} from "./ids"
import { ExclusiveKey, LendCandidate, Placement, Unmet } from "./placement"
import { RunResources } from "./resources"
import { RoutingChoice, RoutingRule } from "./routing"
import { AccessRequest } from "./runner"
import {
  BillingClass,
  ComputeUsage,
  CostSource,
  FundedBy,
  ModelTotals,
  ReportedCost,
  VendorUnits,
} from "./usage"

// #region run-kinds
/**
 * Every model call is a run, and so is every outward execution. A run is ONE attempt.
 * turn: one thinking turn of a persistent agent (sink: its Agent DO).
 * worker: one attempt at an item (sink: a Run DO).
 * validator: one review of a subject run, other model family by default (sink: a Run DO).
 * publish: executes exactly one human-approved outward tool call; no model (gateway, or a runner for stdio MCP).
 */
export const RunKind = Schema.Literals(["turn", "worker", "validator", "publish"])
export const WorkScope = Schema.Struct({
  exclusive: Schema.Boolean,
  requests: Schema.Array(Schema.Struct({ requestId: WorkRequestId, revision: PosInt })),
})
export type RunKind = typeof RunKind.Type

export const Executor = Schema.TaggedUnion({
  runner: { runnerId: RunnerId, profileId: ProfileId, agent: AgentKind }, // includes one_job sandbox runners
  platform: { provider: ModelProvider, model: Schema.String, credentialId: Schema.NullOr(CredentialId) },
  gateway: {}, // publish runs for http integrations (Phase 2)
  hosted: { vendor: Schema.Literals(["anthropic_managed"]), externalId: Schema.String }, // later
})

/** Mechanical: why the process stopped. Set by the driver, the runner or the cloud — NEVER by the model. */
export const StopReason = Schema.Literals([
  "completed", // the model ended its turn on its own (Claude end_turn / Codex turn completed)
  "interrupted", // owner pressed stop, or a steer with priority "now" replaced the turn
  "max_turns", // truncated by the turn/step limit (the brief's "truncated")
  "context_exhausted", // ran out of context without a successful compaction
  "wall_clock", // exceeded the job's wall-clock limit
  "rate_limited", // provider quota on the profile
  "budget_exhausted", // reserved for v2 budgets; recorded so caps need no migration
  "auth_error", // CLI logged out / 401
  "policy_refused", // runner ceiling or permission profile refused the job
  "permission_denied", // a required tool permission was denied and the agent could not continue
  "sandbox_error", // worktree / sandbox / environment could not be prepared (after MAX_PRESTART_RELEASES)
  "cli_error", // driver crash, non-zero exit, protocol violation
  "runner_lost", // lease expired after the run started (cloud reaper)
  "superseded", // a newer epoch exists (a zombie reported)
  "cancelled", // cancelled before it started
  "mcp_unavailable", // Minga's own MCP server did not connect in the CLI, so the run could never report; it ended before the prompt
  // Wave 3 (lessons 1–4): the CLI produced nothing for the job's `limits.idleMs` (not awaiting a permission), so the
  // runner interrupted it. Only a runner with the feature `no_progress` sends it, and only when the job set the limit.
  "no_progress",
])
export type StopReason = typeof StopReason.Type

/** Semantic: what the agent claims, via the `report_outcome` MCP tool (last call wins). A claim, not a fact. */
export const RunOutcome = Schema.TaggedUnion({
  done: {
    summary: Schema.String,
    evidence: Schema.NonEmptyArray(Evidence),
    /** Decision 0101: what the work delivered (absent = verified). */
    result: Schema.optionalKey(CloseResult),
  }, // → in_review, never straight to done
  progress: { summary: Schema.String, next: Schema.String }, // `next` seeds the next attempt's handoff
  blocked: { summary: Schema.String, blocker: Blocker },
  question: { askId: CommentId }, // the ask comment; its answer wakes the next attempt
  impossible: { reason: ImpossibleReason, explanation: Schema.String }, // first-class good exit
  unreported: {}, // never called report_outcome; never counts as done (normal for chat turns)
})
export type RunOutcome = typeof RunOutcome.Type

/** Validator runs only (`report_verdict`). The validator never sees the author's outcome summary. */
export const Verdict = Schema.TaggedUnion({
  pass: {
    evidence: Schema.NonEmptyArray(Evidence),
    /**
     * Decision 0147: minor findings on a pass (the item still passes; its owner decides about follow-ups). Absent = none.
     * Cloud-only: the runner never decodes a verdict (it only sees that `report_verdict` was accepted).
     */
    notes: Schema.optionalKey(Schema.Array(Schema.Struct({ text: Schema.String }))),
  },
  fail: {
    findings: Schema.NonEmptyArray(
      Schema.Struct({ severity: Schema.Literals(["blocker", "major", "minor"]), text: Schema.String }),
    ),
  },
  inconclusive: { why: Schema.String },
})

/** Recorded on RunEnded whenever the accepted outcome differs from the claim. */
export const Downgrade = Schema.Struct({
  from: Schema.Literals(["done", "progress", "blocked", "question", "impossible"]),
  rule: Schema.Literals(["not_completed", "evidence_missing", "evidence_unverifiable"]),
  /** What exactly was missing ("no evidence for the command check …"); absent on runs ended before the sim fixes wave. */
  detail: Schema.optionalKey(Schema.String),
})

export const RunError = Schema.Struct({ code: Schema.String, message: Schema.String, fingerprint: Sha256 })

/**
 * Decision 0095: where a runner run ran, frozen at its end by Minga (the hub's runner record and lease), never by the
 * worker: runner and its version, profile, CLI and its version, model, the account it pays with (a saved subscription
 * or an opaque account key) and the profile's access/sandbox mode. Every field but the runner is optional.
 */
export const RunPlace = Schema.Struct({
  runnerId: RunnerId,
  runner: Schema.String,
  runnerVersion: Schema.optionalKey(Schema.String),
  profileId: Schema.optionalKey(ProfileId),
  cli: Schema.optionalKey(AgentKind),
  cliVersion: Schema.optionalKey(Schema.String),
  model: Schema.optionalKey(Schema.String),
  /** "subscription max-team", "subscription · account 1a2b3c4d", "api_key". */
  account: Schema.optionalKey(Schema.String),
  /** "auto", "full", "ask"; plus "sandbox on"/codex sandbox when set ("auto · codex workspace-write"). */
  access: Schema.optionalKey(Schema.String),
})
export type RunPlace = typeof RunPlace.Type

/**
 * Decision 0155: when a `rate_limited` run's limit resets (epoch ms) and which window ran out (`5h`, `24h`, `7d`; absent
 * when unknown), as the hub learned it from the provider's words or the profile's quota. A worker continues by itself
 * at the reset; a lead's turn is retried then.
 */
export const RateLimitReset = Schema.Struct({
  resetsAt: Millis,
  window: Schema.optionalKey(Schema.Literals(["5h", "24h", "7d"])),
})
export type RateLimitReset = typeof RateLimitReset.Type
// #endregion run-kinds

// #region usage
/**
 * Recorded on every run from day one; v2 budgets plug in without a migration. Unknown cost is null and treated as HIGH.
 *
 * Wave F (decision 0062): `inputTokens` keeps each SOURCE's own meaning in storage and on the wire (as v1 rows do):
 * Claude runner rows uncached; Codex rows including cached input (and cache writes); AI SDK mind rows the AI SDK total
 * (including cache reads and writes). Readers never use it raw: core `usageRowV2` normalizes every row to uncached input
 * from keys every cloud version keeps (`provider`, `profileId`). `cachedInputTokens` = cache reads.
 * Legacy `billing` stays (an older cloud must decode a new runner's rows) and is written from `billingClass` by core
 * `legacyBilling`; it is never evidence of how a row was paid.
 */
/**
 * Decision 0145: the context of a run's main-loop model calls (input + cache reads + cache writes per call): how many,
 * the first, the last and the largest. The first call of a resumed CLI session holds the session so far plus this
 * run's prompt; the last shows how far the run grew it. Runners with `RUNNER_FEATURES.usage_calls` (0.9.23) report it
 * on the run's primary row; the cloud mind on its row.
 */
export const CallContext = Schema.Struct({
  count: NonNegInt,
  first: NonNegInt,
  last: NonNegInt,
  max: NonNegInt,
})
export type CallContext = typeof CallContext.Type

export const Usage = Schema.Struct({
  provider: UsageProvider,
  model: Schema.String,
  profileId: Schema.NullOr(ProfileId),
  billing: Schema.Literals(["subscription_included", "metered", "unpriced"]), // never record subscription cost as $0
  inputTokens: NonNegInt,
  outputTokens: NonNegInt,
  cachedInputTokens: NonNegInt,
  reasoningTokens: NonNegInt,
  /** Marginal money actually charged; null = nothing Minga knows of was charged (included use) or unknown. Never 0 for included use. */
  costMicroUsd: Schema.NullOr(NonNegInt),
  wallMs: NonNegInt,
  turns: NonNegInt,
  toolCalls: NonNegInt,
  // ---- wave F (decision 0062); all optional ----
  /** How this row is paid. Absent on rows before wave F: read as core `usageRowV2` says. */
  billingClass: Schema.optionalKey(BillingClass),
  /** Cache writes, in addition to `cachedInputTokens` (= cache reads). */
  cacheWriteTokens: Schema.optionalKey(NonNegInt),
  /** The part of `cacheWriteTokens` written with a 1-hour TTL (Anthropic prices 5 m and 1 h writes differently). */
  cacheWrite1hTokens: Schema.optionalKey(NonNegInt),
  webSearchRequests: Schema.optionalKey(NonNegInt),
  speed: Schema.optionalKey(Schema.Literals(["standard", "fast"])),
  /** The CLI's own cost estimate for this row, as a per-run delta (Claude `modelUsage[m].costUSD`). */
  reported: Schema.optionalKey(ReportedCost),
  /** What the row would cost at API list price; null = the model is not in the table and the CLI said nothing. */
  listMicroUsd: Schema.optionalKey(Schema.NullOr(NonNegInt)),
  costSource: Schema.optionalKey(CostSource),
  /** The core price table consulted (`PRICE_TABLE_VERSION`). */
  priceTableVersion: Schema.optionalKey(Schema.NullOr(Schema.String)),
  vendorUnits: Schema.optionalKey(VendorUnits),
  /** Overrides Run.fundedBy for this row (later; not produced in wave F). */
  fundedBy: Schema.optionalKey(FundedBy),
  // ---- agent tool friction (decision 0094); cloud turns only, optional ----
  /** Tool calls that returned a rejection (`ok: false`). */
  toolErrors: Schema.optionalKey(NonNegInt),
  /** Per tool: calls, rejected calls and the rejection tags seen (`Forbidden:policy_never`, `Invalid:limit`). */
  tools: Schema.optionalKey(
    Schema.Record(
      Schema.String,
      Schema.Struct({ calls: NonNegInt, errors: NonNegInt, tags: Schema.Array(Schema.String) }),
    ),
  ),
  /** Decision 0145: the context per main-loop model call (primary row only; optional, additive). */
  calls: Schema.optionalKey(CallContext),
})
export type Usage = typeof Usage.Type

/** Live token counts of a run in progress (heartbeat `usageSoFar`, `RunActivity.usage`). Never a cost. */
export const ActivityUsage = Schema.Struct({
  inputTokens: NonNegInt,
  outputTokens: NonNegInt,
  cachedInputTokens: NonNegInt,
  reasoningTokens: NonNegInt,
  /** Wave F: cache writes so far. */
  cacheWriteTokens: Schema.optionalKey(NonNegInt),
  /** Wave F: uncached input + cache writes + output, computed by the runner (absent: an older runner). */
  freshTokens: Schema.optionalKey(NonNegInt),
})
export type ActivityUsage = typeof ActivityUsage.Type

/** Frozen when the run is requested, so cost attribution survives later re-parenting. */
export const Attribution = Schema.Struct({
  goalIds: Schema.Array(GoalId),
  /** The top-level item above the run's item (the item itself when top-level); was `initiativeId`. */
  topItemId: Schema.NullOr(ItemId),
  areaId: Schema.NullOr(AreaId),
  itemId: Schema.NullOr(ItemId),
  agentId: Schema.NullOr(AgentId),
})
// #endregion usage

// #region session
/**
 * Native CLI session pointer: a cache, never the source of truth (the body's transcript is).
 * Claude: the body-backed SessionStore is keyed by sessionId (proven); projectKey is pinned to the agent or item id
 * via CLAUDE_CODE_PROJECT_DIR_NAME (only honoured when CLAUDE_CONFIG_DIR is set; must match ^[A-Za-z0-9_-]{1,64}$).
 * `resumableOn` becomes "anywhere" only once the mirror is confirmed complete (no mirror_error seen).
 * Codex: rollouts live under one CODEX_HOME, so native resume only on the same runner + profile.
 */
export const SessionRef = Schema.TaggedUnion({
  claude: {
    sessionId: Schema.String,
    projectKey: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,64}$/)),
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literals(["anywhere", "same_profile"]),
    /** Wave F: the session's cumulative `modelUsage` after this run, so the next run can take its per-run delta. */
    usageTotals: Schema.optionalKey(Schema.Array(ModelTotals)),
  },
  codex: {
    threadId: Schema.String,
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literal("same_profile"),
  },
  /** Decision 0109: an `agy` conversation (`--conversation <id>`); only ever sent back to the profile that wrote it. */
  antigravity: {
    conversationId: Schema.String,
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literal("same_profile"),
  },
  /** Decision 0110: a Muse Code session (`muse serve`, MSP), resumed natively on the same profile. */
  muse: {
    sessionId: Schema.String,
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literal("same_profile"),
  },
  platform: { throughMessageId: Schema.String }, // the body's own transcript cursor
})
export type SessionRef = typeof SessionRef.Type

/** What the next mind gets when it can't resume natively, or after other minds ran. Typed fields, never free text
 * from a possibly tainted run. Validator findings travel here into the next attempt. */
export const Handoff = Schema.Struct({
  fromRunIds: Schema.Array(RunId),
  mode: Schema.Literals(["delta", "full"]),
  goals: Schema.Array(Schema.String),
  decisions: Schema.Array(Schema.String),
  filesChanged: Schema.Array(Schema.String),
  openApprovals: Schema.Array(ProposalId),
  todos: Schema.Array(Schema.String),
  findings: Schema.Array(Schema.String),
})
// #endregion session

// #region run
/** Every runner→cloud write carries this pair. Every sink rejects epoch < its highest seen, or an ended run. */
export const LeaseRef = Schema.Struct({ runId: RunId, epoch: Epoch })

export const Lease = Schema.Struct({
  runId: RunId,
  epoch: Epoch,
  runnerId: RunnerId,
  runnerIncarnation: NonNegInt,
  profileId: ProfileId,
  grantedAt: Millis,
  expiresAt: Millis, // grantedAt + 60 s, pushed forward by Renew
  exclusive: Schema.Array(ExclusiveKey),
})

export const RunPhase = Schema.Literals(["preparing", "running", "awaiting_permission", "finishing"])

/** The Project DO's view. "offered" is hub-internal (≤ 10 s) and never reaches the log. */
export const RunState = Schema.TaggedUnion({
  requested: {},
  /** "waiting for runner", with reasons; `lendable` (wave E): runners whose owners could lend them to the item. */
  waiting: {
    unmet: Schema.Array(Unmet),
    since: Millis,
    lendable: Schema.optionalKey(Schema.Array(LendCandidate)),
  },
  leased: { epoch: Epoch, phase: RunPhase }, // phase "preparing" may be re-leased; from "running" on, loss ends the run
  ended: { stopReason: StopReason },
})

/** The newest milestone of a run, as `Run.lastMilestone` keeps it. */
export const RunMilestoneView = Schema.Struct({
  kind: Schema.String,
  text: Schema.String,
  ref: Schema.NullOr(Schema.String),
  at: Millis,
})

/**
 * Worker runs on runner ≥ 0.7.0 (decision 0059): the worktree's HEAD at the end and at the start, and `origin/<branch>`
 * at the end and at the start (null when there is no such ref). A run pushed when `remoteSha !== startRemoteSha`; it may
 * have committed more on top without pushing (`sha ≠ remoteSha`).
 */
export const RunHead = Schema.Struct({
  sha: GitSha,
  startSha: GitSha,
  remoteSha: Schema.NullOr(GitSha),
  startRemoteSha: Schema.NullOr(GitSha),
})
export type RunHead = typeof RunHead.Type

/** A worker or validator run as a temporary teammate (settings wave S7, decision 0025). */
export const RunWorker = Schema.Struct({
  ref: WorkerRef,
  /** The item's owner agent: the persistent agent who supervises this run. */
  supervisorId: AgentId,
  /** The resolved worker preset's key (null only for runs without a preset). */
  preset: Schema.NullOr(Slug),
})
export type RunWorker = typeof RunWorker.Type

/** Where the run streams. The runner always dials it; no DO ever dials out. */
export const RunSink = Schema.TaggedUnion({ agent: { agentId: AgentId }, run_do: {}, none: {} })

/** Who started a turn (decision 0048): a person (full policy) or changes (the agent's autonomy level applies). */
export const TurnOrigin = Schema.Literals(["person", "changes"])
export type TurnOrigin = typeof TurnOrigin.Type

export const Run = Schema.Struct({
  ...Owned, // ownerId = principal: whose runners and subscriptions may execute it (owner lock). Frozen at creation.
  id: RunId,
  ref: ShortRef, // R-2291
  kind: RunKind,
  agentId: Schema.NullOr(AgentId), // turn: the agent; worker/validator: the item's owner agent
  itemId: Schema.NullOr(ItemId),
  subjectRunId: Schema.NullOr(RunId), // validator: the run under review
  publish: Schema.NullOr(Schema.Struct({ proposalId: ProposalId, revision: Schema.Int })),
  wakeBatchId: Schema.NullOr(WakeId), // turn: the coalesced wakes it answers
  attempt: PosInt, // per item for workers; per review round for validators; 1 for turns and publish
  /** worker (itemId, attempt) · validator (itemId, round, n) · turn (agentId, wakeBatchId) · publish (proposalId, revision). Old runs keep `worker:<tsk_…>:n`. */
  idempotencyKey: Schema.String,
  attribution: Attribution,
  placement: Placement, // frozen effective placement: area defaults ⊕ item ⊕ mind ⊕ system constraints
  hands: Hands, // frozen
  context: Schema.NullOr(
    Schema.Struct({
      hash: Sha256,
      manifestRef: Schema.String,
      taint: Taint,
      documents: Schema.optionalKey(Schema.Array(LoadedDocument)),
    }),
  ),
  sink: RunSink,
  state: RunState,
  epoch: Epoch, // highest epoch seen (0 until the first lease)
  prestartReleases: NonNegInt, // re-leases before "running"; capped by MAX_PRESTART_RELEASES
  executor: Schema.NullOr(Executor),
  claimed: Schema.NullOr(RunOutcome), // latest report_outcome claim
  verdict: Schema.NullOr(Verdict), // validators
  outcome: Schema.NullOr(RunOutcome), // the ACCEPTED outcome, set at RunEnded
  downgrade: Schema.NullOr(Downgrade),
  stopReason: Schema.NullOr(StopReason),
  error: Schema.NullOr(RunError),
  /** Decision 0095: where it ran, recorded by Minga at its end (runner runs only). */
  place: Schema.optionalKey(RunPlace),
  usage: Schema.Array(Usage),
  sessionIn: Schema.NullOr(SessionRef),
  sessionOut: Schema.NullOr(SessionRef),
  taint: Taint, // only ever rises during a run
  requestedAt: Millis,
  startedAt: Schema.NullOr(Millis),
  endedAt: Schema.NullOr(Millis),
  // Display facts (fix wave 2026-10-09 §4.2, decision 0022). Optional so old snapshots decode; never read by decide.
  /** RunLeased's time: "preparing since". */
  leasedAt: Schema.optionalKey(Millis),
  /** Start of the current phase: RunLeased, RunStarted, and a RunMilestone that changes the phase. */
  phaseSince: Schema.optionalKey(Millis),
  /** The newest RunMilestone (every kind); survives a reload. */
  lastMilestone: Schema.optionalKey(RunMilestoneView),
  /** The worktree branch from the frozen job (RunRequested.branch); null = scratch or no workspace. */
  branch: Schema.optionalKey(Schema.NullOr(Schema.String)),
  // Settings wave (§5.3). Set by decide at RequestRun; never read by decide afterwards.
  /** Workers and validators: the temporary teammate this run is. */
  worker: Schema.optionalKey(RunWorker),
  /** The narrowing request frozen with the run (turns: core K1; workers/validators: the preset). Only when it narrows. */
  access: Schema.optionalKey(AccessRequest),
  /** Workers/validators: model and effort per CLI family, for a run whose family the lease decides. */
  byAgent: Schema.optionalKey(ModelsByAgent),
  /** The workflow rule frozen when this attempt was requested. */
  routing: Schema.optionalKey(RoutingRule),
  routeChoice: Schema.optionalKey(RoutingChoice),
  /** Who asked to stop it (decision 0032; RunCancelRequested's actor): display and the stop's `ItemPaused.by`. */
  cancelRequestedBy: Schema.optionalKey(Schema.NullOr(Principal)),
  /** A requested stop is not a terminal run state. The runner's final reports the actual stop. */
  cancelRequestedAt: Schema.optionalKey(Millis),
  /** Decision 0116: stopped before it started because its item's placement or route changed: re-dispatched, unpaused. */
  cancelRedispatch: Schema.optionalKey(Schema.Boolean),
  /** Workers and validators: the item description's revision the run read (`spec_changed` compares against it). */
  specRev: Schema.optionalKey(PosInt),
  /**
   * Turn runs (decision 0048): who started the turn. Absent = "person" (runs before this wave). Set by decide from
   * RequestRun; never read by decide except autonomyOf.
   */
  startedBy: Schema.optionalKey(TurnOrigin),
  // Wave A. Set by decide / evolve; never read by decide except `chainOf` (reactions) and `madeProgress`.
  /** Turn runs: the chain of the wake batch it answers (core `batchChain`). Absent = 0. */
  chain: Schema.optionalKey(NonNegInt),
  /** Worker runs: the first `pr_opened` milestone of this run (v2 events only): the attempt pushed. */
  pushedAt: Schema.optionalKey(Millis),
  /**
   * Worker runs on runner ≥ 0.7.0 (decision 0059): the worktree's HEAD at the end and at the start, and
   * `origin/<branch>` at the end and at the start (null when there is no such ref). Set at RunEnded.
   */
  head: Schema.optionalKey(RunHead),
  // Wave F (decision 0062). Never read by decide.
  /**
   * Who asked (frozen at RequestRun): the person whose ask this run serves, or the agent on her own initiative. Not the
   * payer (`fundedBy`) and not `ownerId` (who may execute). Absent on runs before wave F.
   */
  askedBy: Schema.optionalKey(Principal),
  /** Whose plan or key paid (set at RunEnded). Absent: before wave F, or ended without a runner's Final. */
  fundedBy: Schema.optionalKey(FundedBy),
  /** Sandbox compute (later; platform runners). */
  compute: Schema.optionalKey(Schema.Array(ComputeUsage)),
  /** Decision 0149: what the run used (set at RunEnded from the runner's sampler). Never read by decide. */
  resources: Schema.optionalKey(RunResources),
  // Wave C (decision 0071). Never read by decide.
  /**
   * Turn runs: the wake batch's reasons as delivered (at most 10, the first ones), set by the Project DO at RequestRun
   * from the fresh batch, so the agent page's "Since her last turn" reads them without the R2 pack. Absent on runs
   * before wave C and on non-turn runs.
   */
  wakeReasons: Schema.optionalKey(Schema.Array(WakeReason).check(Schema.isMaxLength(10))),
  /** Full batch scope, recorded in RunRequested; display wakeReasons are never authority to interrupt a turn. */
  workScope: Schema.optionalKey(WorkScope),
  /** Decision 0135: when the watchdog last noted this run quiet (RunQuietNoted). Never read by decide but NoteRunQuiet. */
  quietAt: Schema.optionalKey(Millis),
})
export type Run = typeof Run.Type
// #endregion run
